/**
 * Cliente HTTP de ÁGORA.
 *
 * - Base fija '/api/v1' (Apache hace ProxyPass /api -> 127.0.0.1:8020).
 * - Envía cookies (credentials: 'include') para el refresh httpOnly de sala/OAuth.
 * - Adjunta el access token en memoria como Bearer (nunca a localStorage: C5).
 * - En 401 intenta UN refresh silencioso vía POST /auth/refresh y reintenta.
 *
 * El authStore registra el proveedor de token y el callback de logout con
 * `configureApi` para evitar dependencias circulares con zustand.
 */

export const API_BASE = '/api/v1';

/** Error de API con estado y detalle ya legible en español cuando existe. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

type TokenProvider = () => string | null;

interface ApiConfig {
  /** Devuelve el access token vigente (en memoria) o null. */
  getToken: TokenProvider;
  /** Guarda un token recién refrescado en el store. */
  onTokenRefreshed: (token: string) => void;
  /** Se llama cuando el refresh también falla: fuerza cierre de sesión. */
  onAuthLost: () => void;
}

let config: ApiConfig = {
  getToken: () => null,
  onTokenRefreshed: () => {},
  onAuthLost: () => {},
};

export function configureApi(next: ApiConfig): void {
  config = next;
}

/** Traduce fallos de red/HTTP a un mensaje humano en español. */
function humanMessage(status: number, raw: string | undefined): string {
  if (raw && raw.trim()) return raw;
  if (status === 0) return 'No pudimos conectar. Revisá tu red e intentá de nuevo.';
  if (status === 401) return 'Tu sesión venció. Volvé a ingresar.';
  if (status === 403) return 'No tenés permiso para esta acción.';
  if (status === 404) return 'No encontramos lo que buscabas.';
  if (status === 429) return 'Demasiados intentos. Esperá un momento e intentá otra vez.';
  if (status >= 500) return 'Algo falló de nuestro lado. Intentá de nuevo en un momento.';
  return 'No pudimos completar la acción. Intentá de nuevo.';
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Extrae el `detail` de FastAPI (string o lista de errores de validación). */
function extractDetail(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') {
    return typeof body === 'string' ? body : undefined;
  }
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: string };
    return first?.msg;
  }
  return undefined;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Cuerpo JSON; se serializa y fija el Content-Type automáticamente. */
  json?: unknown;
  /**
   * Cuerpo multipart (subida de media M2). Se envía tal cual: el navegador fija
   * el Content-Type con su boundary — NO lo seteamos a mano.
   */
  form?: FormData;
  /** Si es true, no intenta refrescar el token en un 401 (evita bucles). */
  skipRefresh?: boolean;
}

async function rawRequest(path: string, options: RequestOptions): Promise<Response> {
  const { json, form, headers, skipRefresh: _skip, ...rest } = options;
  const finalHeaders = new Headers(headers);
  finalHeaders.set('Accept', 'application/json');

  const token = config.getToken();
  if (token) finalHeaders.set('Authorization', `Bearer ${token}`);

  let body: BodyInit | undefined;
  if (json !== undefined) {
    finalHeaders.set('Content-Type', 'application/json');
    body = JSON.stringify(json);
  } else if (form !== undefined) {
    // Sin Content-Type manual: el boundary lo pone el navegador.
    body = form;
  }

  return fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: finalHeaders,
    body,
    ...rest,
  });
}

/**
 * Refresh en vuelo, si lo hay. El backend ROTA el refresh token en cada
 * llamada: dos refrescos concurrentes (dos 401 en paralelo, o un 401 y el
 * WebSocket a la vez) se pisan la cookie y el segundo muere con 401. Por eso
 * hay UN solo refresh a la vez y todos esperan el mismo resultado.
 */
let refreshEnVuelo: Promise<boolean> | null = null;

async function pedirRefresh(): Promise<boolean> {
  try {
    const res = await rawRequest('/auth/refresh', {
      method: 'POST',
      skipRefresh: true,
    });
    if (!res.ok) return false;
    const body = (await parseBody(res)) as { access_token?: string } | null;
    if (body?.access_token) {
      config.onTokenRefreshed(body.access_token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** Intenta un refresh silencioso. Devuelve true si obtuvo un token nuevo. */
async function tryRefresh(): Promise<boolean> {
  if (refreshEnVuelo) return refreshEnVuelo;
  refreshEnVuelo = pedirRefresh().finally(() => {
    refreshEnVuelo = null;
  });
  return refreshEnVuelo;
}

/**
 * Renueva el access token contra la cookie httpOnly, fuera del ciclo de un
 * request. Lo usa el WebSocket de la sala viva: su token viaja en el query del
 * handshake, así que cuando vence NADIE lo refresca por él — el socket se
 * quedaba reintentando con un JWT muerto para siempre (jornada CCJ del 6-ago:
 * 2 169 handshakes rechazados con el mismo token vencido).
 *
 * Comparte el single-flight con el refresh de los 401 de {@link apiFetch}.
 */
export function refrescarToken(): Promise<boolean> {
  return tryRefresh();
}

/**
 * Petición JSON tipada. Lanza {@link ApiError} con mensaje humano en fallo.
 * `T = void` para respuestas sin cuerpo.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  let res: Response;
  try {
    res = await rawRequest(path, options);
  } catch {
    throw new ApiError(0, humanMessage(0, undefined));
  }

  // 401 -> un intento de refresh + reintento, salvo que se pida omitirlo.
  if (res.status === 401 && !options.skipRefresh) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      try {
        res = await rawRequest(path, options);
      } catch {
        throw new ApiError(0, humanMessage(0, undefined));
      }
    } else {
      config.onAuthLost();
    }
  }

  const body = await parseBody(res);

  if (!res.ok) {
    throw new ApiError(res.status, humanMessage(res.status, extractDetail(body)));
  }

  return body as T;
}

/**
 * Descarga un recurso NO-JSON (p. ej. text/markdown de export.md) como Blob.
 * Reusa el mismo Bearer + refresh silencioso que {@link apiFetch}, pero no
 * intenta parsear el cuerpo: lo devuelve tal cual para gatillar una descarga.
 */
export async function apiDownload(
  path: string,
  options: RequestOptions = {},
): Promise<Blob> {
  let res: Response;
  try {
    res = await rawRequest(path, options);
  } catch {
    throw new ApiError(0, humanMessage(0, undefined));
  }

  if (res.status === 401 && !options.skipRefresh) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      try {
        res = await rawRequest(path, options);
      } catch {
        throw new ApiError(0, humanMessage(0, undefined));
      }
    } else {
      config.onAuthLost();
    }
  }

  if (!res.ok) {
    const body = await parseBody(res);
    throw new ApiError(res.status, humanMessage(res.status, extractDetail(body)));
  }

  return res.blob();
}
